import { useState } from 'react';

import PlayButton from 'v3/components/PlayButton';
import { FLOW_SCREEN_Z_INDEX } from 'v3/components/FlowScreen/styles';

import * as S from './styles';

/**
 * Explainer video for a step, shown as a poster in the page and played in a modal.
 *
 * The poster stays in the card so the video is visibly on offer, but playing it
 * opens a modal — inside the card the frame is capped by whatever column it sits
 * in, which for the offer step is a fraction of the screen.
 *
 * The iframe only exists while the modal is open. That keeps a third-party embed
 * off the page until someone asks for it, and closing the modal unmounts the
 * iframe, which stops playback without having to message the player.
 */

interface VideoPanelProps {
  title: string;
  caption?: string;
  /** Vimeo embed URL, player parameters included. */
  videoLink: string;
  posterImage?: string;
  /** No border, background or radius of its own — for living inside a card that is
   *  already the box. In SignDeal's aside it is a card; in the offer, a section. */
  flat?: boolean;
  /** 'row' puts the frame left and the text right — far shorter inside a wide
   *  card. 'column' stacks, for narrow columns. */
  orientation?: 'row' | 'column';
}

const VideoPanel = ({
  title,
  caption,
  videoLink,
  posterImage,
  flat = false,
  orientation = 'column',
}: VideoPanelProps) => {
  const [playing, setPlaying] = useState(false);
  const row = orientation === 'row';

  return (
    <S.Panel $flat={flat} $row={row}>
      <S.Frame $poster={posterImage} $row={row}>
        <S.Veil />
        <S.PlayLayer>
          <PlayButton
            variant="secondary"
            size="large"
            onClick={() => setPlaying(true)}
          />
        </S.PlayLayer>
      </S.Frame>

      <S.Modal
        open={playing}
        onCancel={() => setPlaying(false)}
        footer={null}
        centered
        destroyOnClose
        width={860}
        title={null}
        /* The step renders on a fixed full-bleed screen, so antd's default 1000
           would put the modal underneath it — open, but invisible. */
        zIndex={FLOW_SCREEN_Z_INDEX + 100}
      >
        <S.ModalFrame>
          {/* Guarded on `playing` as well as the modal's own mount, so the embed is
              never requested for a modal that is closed. */}
          {playing ? (
            <iframe
              src={`${videoLink}&autoplay=1`}
              title={title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          ) : null}
        </S.ModalFrame>
      </S.Modal>

      {/* Text below the player: the frame opens the card and the title explains
          what you just saw. */}
      <S.Footer $flat={flat} $row={row}>
        <S.Title>{title}</S.Title>
        {caption ? <S.Caption>{caption}</S.Caption> : null}
      </S.Footer>
    </S.Panel>
  );
};

export default VideoPanel;
